import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { toast as sonner } from "sonner"

import { Toaster } from "@/components/ui/sonner"

const D = {
  success: 3_000,
  error: 5_000,
  warning: 5_000,
  info: 4_000,
  withUndo: 8_000,
} as const

/**
 * App-wide sonner `Toaster` (top-right, theme-aware). Mount once — e.g. in `main.tsx`.
 */
export function ToastProvider() {
  return <Toaster position="top-right" closeButton offset={12} />
}

/**
 * Centralized toasts: durations match product defaults (3s success, 5s error/warning, 8s undo).
 */
export function useToast() {
  const { t } = useTranslation()

  return useMemo(
    () => ({
      success: (message: string) => sonner.success(message, { duration: D.success }),
      error: (message: string, options?: { description?: string }) =>
        sonner.error(message, { duration: D.error, ...options }),
      warning: (message: string) => sonner.warning(message, { duration: D.warning }),
      info: (message: string) => sonner.info(message, { duration: D.info }),
      successWithUndo: (message: string, onUndo: () => void | Promise<void>) =>
        sonner.success(message, {
          duration: D.withUndo,
          action: {
            label: t("common.undo", { defaultValue: "Undo" }),
            onClick: () => {
              void Promise.resolve(onUndo())
            },
          },
        }),
    }),
    [t]
  )
}
